import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { scalePoint, scoreLabel, type Entry } from '@fatigue/shared';

interface Props {
  readonly entries: readonly Entry[];
  readonly selectedEntryId: string | null;
  readonly onSelect: (entryId: string | null) => void;
}

/**
 * Comments in the visible range, newest first.
 *
 * Clicking one selects the corresponding chart point -- the same selection
 * state, not a parallel one -- and selecting a point on the chart scrolls the
 * matching comment into view here.
 *
 * Comments render as plain text exactly as submitted. No keyword highlighting,
 * no sentiment scoring, no triage. The physiotherapist reads them.
 */
export function CommentList({ entries, selectedEntryId, onSelect }: Props): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  const withComments = [...entries]
    .filter((entry) => entry.comment !== '')
    .sort((a, b) => new Date(b.capturedAt).getTime() - new Date(a.capturedAt).getTime());

  useEffect(() => {
    if (selectedEntryId === null || listRef.current === null) return;
    const element = listRef.current.querySelector(`[data-entry="${CSS.escape(selectedEntryId)}"]`);
    // `nearest` so choosing a comment that is already visible does not jump
    // the list under the reader's eyes.
    element?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedEntryId]);

  if (withComments.length === 0) {
    return <p className="empty">No comments in this period. Widen the range to see earlier notes.</p>;
  }

  return (
    <div className="cmts" ref={listRef}>
      {withComments.map((entry) => {
        const point = scalePoint(entry.score);
        const selected = entry.entryId === selectedEntryId;

        return (
          <button
            key={entry.entryId}
            type="button"
            data-entry={entry.entryId}
            className={`cmt${selected ? ' cmt--on' : ''}`}
            aria-pressed={selected}
            onClick={() => onSelect(entry.entryId)}
          >
            {/* The level's name is in the meta line beside it. */}
            <span className="cmt__pip" style={{ background: point.colour }} />
            <span className="cmt__body">
              <span className="cmt__meta">
                {formatFull(entry.capturedAt)} &middot; {scoreLabel(entry.score)}
              </span>
              <span className="cmt__text">{entry.comment}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

const formatFull = (iso: string): string => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })} · ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};
